import { useEffect, useRef } from 'react';
import { Controller, useWatch } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTrimmedForm } from '@/shared/lib/useTrimmedForm';
import dayjs from 'dayjs';
import {
  AppButton,
  AppCheckbox,
  AppRadio,
  AppRadioGroup,
  AppSelect,
  AppTag,
  AppTextField,
} from '@/components/atoms';
import { AppFormLabel } from '@/components/atoms';
import AddressSearchModal from '@/components/organisms/AddressSearchModal';
import { RA2NDatePicker } from '@/components/organisms';
import { convGtJDate } from '@/utils/dateConverter';
import { convertSeirekiWrap } from '@/utils/wareki';
import { useAddressSearch } from '@/hooks/useAddressSearch';
import { useAddressSearchModalHelper } from '@/hooks/useAddressSearchModalHelper';
import type { ClinicOption, HealthCenterRegistTypeOption } from '@/services/admin/clinic-locations';
import { clinicLocationFormSchema, type ClinicLocationFormData } from './form-schema';
import { useJapaneseNameKanaFields } from '@/hooks/useJapaneseNameKanaFields';
import { normalizePostalCodeInput } from '@/utils/japaneseText';
import { SERVICE_TYPE_A2, SERVICE_TYPE_BOTH, SERVICE_TYPE_R2 } from '@/constants/serviceType';

const RequiredTag = () => <AppTag color="magenta">必須</AppTag>;

/** Initial data for edit mode (API show response shape). */
export type ClinicLocationInitialData = ClinicLocationFormData & {
  id?: number;
  clinic_number?: string;
  serial_number?: number;
  /** From clinic.service_type - for edit mode サービス区分 enable/disable */
  service?: string | null;
  /** Block uncheck A2 when this clinic location has A2 visits */
  has_visits_a2?: boolean;
  /** Block uncheck R2 when this clinic location has R2 visits */
  has_visits_r2?: boolean;
  /** Block uncheck A2 when massagers with A2 service are assigned to this location */
  has_massager_a2?: boolean;
  /** Block uncheck R2 when massagers with R2 service are assigned to this location */
  has_massager_r2?: boolean;
};

type Props = {
  clinics: ClinicOption[];
  healthCenterRegistTypes: HealthCenterRegistTypeOption[];
  preselectedClinicId: number | null;
  clinicIdFromUrl?: number | null;
  clinicNumberDisplay?: string | null;
  initialData?: ClinicLocationInitialData | null;
  editSerialNumber?: number;
  /** Spec: 拠点更新:{clinic_number}_{serial_number} for card title */
  editDisplayId?: string;
  onSubmit: (data: ClinicLocationFormData) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  siteType?: string;
  clinicIdResetTrigger?: number;
  /** API validation errors (e.g. available_from, available_to) to show under fields */
  apiErrors?: Record<string, string>;
  /** When false, hide the 詳細画面へ戻る button (e.g. when clinic_id from URL does not exist in DB) */
  showBackToDetailButton?: boolean;
};

const defaultCreateValues: ClinicLocationFormData = {
  clinic_id: 0,
  service_type: SERVICE_TYPE_A2,
  location_name: '',
  location_name_kana: '',
  available_from: '',
  available_to: '',
  health_center_regist_type_id: undefined as unknown as number,
  postal_code: '',
  address1: '',
  address2: '',
  address3: '',
  telephone: '',
  fax: '',
  rezept_address: '',
  base_name: '',
  is_compnay: null,
  founder_name: '',
  founder_name_kana: '',
  founder_postal_code: '',
  founder_address1: '',
  founder_address2: '',
  founder_address3: '',
  founder_date_of_birth: '',
  founder_telephone: '',
};

function toFormDefaultValues(
  initial: ClinicLocationInitialData | null | undefined,
  preselectedClinicId: number | null,
  clinicIdFromUrl: number | null | undefined,
  clinics: ClinicOption[]
): ClinicLocationFormData {
  if (initial) {
    const serviceType =
      (initial as ClinicLocationFormData & { service_type?: string }).service_type ??
      SERVICE_TYPE_A2;
    const normalized =
      serviceType === SERVICE_TYPE_A2 ||
        serviceType === SERVICE_TYPE_R2 ||
        serviceType === SERVICE_TYPE_BOTH
        ? serviceType
        : SERVICE_TYPE_A2;
    return {
      clinic_id: initial.clinic_id,
      service_type: normalized,
      location_name: initial.location_name ?? '',
      location_name_kana: initial.location_name_kana ?? '',
      available_from: initial.available_from ?? '',
      available_to: initial.available_to ?? '',
      health_center_regist_type_id:
        initial.health_center_regist_type_id ?? (undefined as unknown as number),
      postal_code: initial.postal_code ?? '',
      address1: initial.address1 ?? '',
      address2: initial.address2 ?? '',
      address3: initial.address3 ?? '',
      telephone: initial.telephone ?? '',
      fax: initial.fax ?? '',
      rezept_address: initial.rezept_address ?? '',
      base_name: initial.base_name ?? '',
      is_compnay: initial.is_compnay ?? null,
      founder_name: initial.founder_name ?? '',
      founder_name_kana: initial.founder_name_kana ?? '',
      founder_postal_code: initial.founder_postal_code ?? '',
      founder_address1: initial.founder_address1 ?? '',
      founder_address2: initial.founder_address2 ?? '',
      founder_address3: initial.founder_address3 ?? '',
      founder_date_of_birth: initial.founder_date_of_birth ?? '',
      founder_telephone: initial.founder_telephone ?? '',
    };
  }
  const rawClinicId = clinicIdFromUrl ?? preselectedClinicId ?? clinics[0]?.id ?? 0;
  const clinic = rawClinicId ? findClinicById(clinics, rawClinicId) : undefined;
  const clinicId = clinic ? Number(rawClinicId) : Number(clinics[0]?.id ?? 0);
  const effectiveClinic = clinic ?? clinics[0];
  const derivedServiceType = deriveServiceTypeFromClinic(effectiveClinic?.service_type);
  return {
    ...defaultCreateValues,
    clinic_id: clinicId,
    service_type: derivedServiceType,
  };
}

function deriveServiceTypeFromClinic(
  clinicServiceType: string | null | undefined
): typeof SERVICE_TYPE_A2 | typeof SERVICE_TYPE_R2 | typeof SERVICE_TYPE_BOTH {
  const st = (clinicServiceType ?? '').toUpperCase();
  if (st === SERVICE_TYPE_A2) return SERVICE_TYPE_A2;
  if (st === SERVICE_TYPE_R2) return SERVICE_TYPE_R2;
  if (st === SERVICE_TYPE_BOTH) return SERVICE_TYPE_BOTH;
  return SERVICE_TYPE_BOTH;
}

/** API / Select may coerce ids; strict === breaks 治療院 lookup and サービス区分 UI. */
function findClinicById(clinics: ClinicOption[], id: unknown): ClinicOption | undefined {
  const n = Number(id);
  if (!Number.isFinite(n) || n <= 0) return undefined;
  return clinics.find(c => Number(c.id) === n);
}

function sameClinicId(a: unknown, b: unknown): boolean {
  const na = Number(a);
  const nb = Number(b);
  if (!Number.isFinite(na) || !Number.isFinite(nb)) return false;
  return na === nb;
}

function getServiceTypeFromCheckboxes(
  a2: boolean,
  r2: boolean
): typeof SERVICE_TYPE_A2 | typeof SERVICE_TYPE_R2 | typeof SERVICE_TYPE_BOTH | '' {
  if (a2 && r2) return SERVICE_TYPE_BOTH;
  if (a2) return SERVICE_TYPE_A2;
  if (r2) return SERVICE_TYPE_R2;
  return '';
}

const LABEL_WIDTH = 'w-40 shrink-0';
/** Matches form-schema max for 拠点名 / 拠点名カナ / 開設者名 / 開設者名カナ */
const CLINIC_NAME_FIELD_MAX = 100;

export function CreateClinicLocationForm({
  clinics,
  healthCenterRegistTypes,
  preselectedClinicId,
  clinicIdFromUrl,
  initialData,
  editSerialNumber,
  editDisplayId,
  onSubmit,
  onCancel,
  isLoading = false,
  siteType = 'medix',
  clinicIdResetTrigger = 0,
  apiErrors = {},
  showBackToDetailButton = true,
}: Props) {
  const isEditMode = initialData != null;
  const showClinicIdDisabled = clinicIdFromUrl != null || isEditMode;

  const defaultValues = toFormDefaultValues(
    initialData ?? undefined,
    preselectedClinicId,
    clinicIdFromUrl,
    clinics
  );

  const {
    control,
    handleSubmit,
    getValues,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors, isDirty },
  } = useTrimmedForm<ClinicLocationFormData>({
    resolver: zodResolver(clinicLocationFormSchema),
    defaultValues,
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
  });

  // Clear field error when user types (only show error when submit, clear when typing)
  useEffect(() => {
    const subscription = watch((_, { name }) => {
      if (name) clearErrors(name as Parameters<typeof clearErrors>[0]);
    });
    return () => subscription.unsubscribe();
  }, [watch, clearErrors]);

  const currentClinicId = watch('clinic_id');
  const currentClinic = findClinicById(clinics, currentClinicId);
  // Create: when clinic_id is 0 or not found, fallback to preselected/URL/first so single-service lock works before useEffect runs
  const effectiveClinicId =
    Number(currentClinicId) ||
    (preselectedClinicId ?? 0) ||
    (clinicIdFromUrl ?? 0) ||
    Number(clinics[0]?.id ?? 0);
  const effectiveClinic =
    findClinicById(clinics, effectiveClinicId) ?? currentClinic;
  const clinicServiceType = (effectiveClinic?.service_type ?? '').toUpperCase();
  const isA2Only = clinicServiceType === SERVICE_TYPE_A2;
  const isR2Only = clinicServiceType === SERVICE_TYPE_R2;
  const isSingleServiceClinic = isA2Only || isR2Only;
  const userClearedClinicIdRef = useRef(false);
  const userHasSelectedClinicRef = useRef(false);
  /** Create: last clinic_id we defaulted サービス区分 for; avoids overwriting on options refetch. */
  const prevSyncedClinicIdRef = useRef<number | null>(null);

  useEffect(() => {
    for (const [field, message] of Object.entries(apiErrors)) {
      setError(field as keyof ClinicLocationFormData, { type: 'server', message });
    }
  }, [apiErrors, setError]);

  // Edit: clinic_id のサーバーエラーは親で toast のみにしているため、残った setError を消す
  useEffect(() => {
    if (isEditMode && !('clinic_id' in apiErrors)) {
      clearErrors('clinic_id');
    }
  }, [isEditMode, apiErrors, clearErrors]);

  useEffect(() => {
    if (clinicIdResetTrigger > 0 && clinics.length > 0) {
      const firstId = clinics[0].id;
      setValue('clinic_id', firstId, { shouldValidate: false });
      const firstClinic = clinics[0];
      setValue('service_type', deriveServiceTypeFromClinic(firstClinic?.service_type), {
        shouldValidate: false,
      });
    }
  }, [clinicIdResetTrigger, clinics, setValue]);

  // Create: set default サービス区分 when clinic_id changes or options first load — not on every clinics[] refetch.
  useEffect(() => {
    if (isEditMode) return;
    const idNum = Number(currentClinicId);
    if (!Number.isFinite(idNum) || idNum <= 0 || clinics.length === 0) return;
    const clinic = findClinicById(clinics, idNum);
    if (!clinic) return;
    const prev = prevSyncedClinicIdRef.current;
    if (prev === idNum) return;
    prevSyncedClinicIdRef.current = idNum;
    setValue('service_type', deriveServiceTypeFromClinic(clinic.service_type), {
      shouldValidate: false,
    });
  }, [currentClinicId, clinics, isEditMode, setValue]);

  // A2-only/R2-only: lock service_type (sync with 治療院編集 - when clinic service changes)
  useEffect(() => {
    if (isSingleServiceClinic && currentClinicId) {
      const forced = isA2Only ? SERVICE_TYPE_A2 : SERVICE_TYPE_R2;
      setValue('service_type', forced, { shouldValidate: false });
    }
  }, [isA2Only, isR2Only, isSingleServiceClinic, currentClinicId, setValue]);

  // Edit mode: sync service_type when clinic has single service (from 治療院編集 update)
  useEffect(() => {
    if (!isEditMode || !initialData?.service) return;
    const st = String(initialData.service).toUpperCase();
    if (st === SERVICE_TYPE_A2 || st === SERVICE_TYPE_R2) {
      setValue('service_type', st === SERVICE_TYPE_A2 ? SERVICE_TYPE_A2 : SERVICE_TYPE_R2, {
        shouldValidate: false,
      });
    }
  }, [isEditMode, initialData?.service, setValue]);
  // When clinic_id is not in options (e.g. stale list), auto-set.
  // Skip override when user deliberately cleared via ant-select-clear (X) or has selected from dropdown.
  useEffect(() => {
    if (clinics.length === 0) return;
    if (userClearedClinicIdRef.current) {
      userClearedClinicIdRef.current = false;
      return;
    }
    if (userHasSelectedClinicRef.current) return;
    const idNum = Number(currentClinicId);
    const isValidCurrent = idNum > 0 && clinics.some(c => Number(c.id) === idNum);
    if (isValidCurrent) return;
    if (isDirty && idNum > 0) return;
    const targetId =
      clinicIdFromUrl != null && clinics.some(c => sameClinicId(c.id, clinicIdFromUrl))
        ? Number(clinicIdFromUrl)
        : Number(clinics[0].id);
    prevSyncedClinicIdRef.current = null;
    setValue('clinic_id', targetId, { shouldValidate: true });
    const firstClinic = findClinicById(clinics, targetId) ?? clinics[0];
    setValue('service_type', deriveServiceTypeFromClinic(firstClinic?.service_type), {
      shouldValidate: false,
    });
    prevSyncedClinicIdRef.current = targetId;
  }, [clinics, currentClinicId, clinicIdFromUrl, isDirty, setValue]);

  const locationNameKanaValue = watch('location_name_kana');
  const founderNameKanaValue = watch('founder_name_kana');

  const locationNameKanaFields = useJapaneseNameKanaFields({
    nameKanaValue: String(locationNameKanaValue ?? ''),
    setNameValue: v => setValue('location_name', v, { shouldDirty: true, shouldValidate: false }),
    setKanaValue: v =>
      setValue('location_name_kana', v, { shouldDirty: true, shouldValidate: false }),
    maxLength: CLINIC_NAME_FIELD_MAX,
  });

  const founderNameKanaFields = useJapaneseNameKanaFields({
    nameKanaValue: String(founderNameKanaValue ?? ''),
    setNameValue: v => setValue('founder_name', v, { shouldDirty: true, shouldValidate: true }),
    setKanaValue: v =>
      setValue('founder_name_kana', v, { shouldDirty: true, shouldValidate: true }),
    maxLength: CLINIC_NAME_FIELD_MAX,
  });

  const postalCode = useWatch({ control, name: 'postal_code', defaultValue: '' });
  const founderPostalCode = useWatch({ control, name: 'founder_postal_code', defaultValue: '' });
  const address1 = useWatch({ control, name: 'address1', defaultValue: '' });
  const founderAddress1 = useWatch({ control, name: 'founder_address1', defaultValue: '' });

  const addressSearchMutation = useAddressSearch({
    siteType: siteType ?? 'medix',
    isAdmin: true,
  });

  const {
    isAddressModalOpen,
    closeAddressModal,
    handleAddressButtonClick,
    handleAddressSelect,
    modalInput,
  } = useAddressSearchModalHelper<ClinicLocationFormData>({
    postalCodeValue: String(postalCode ?? ''),
    addressValue: String(address1 ?? ''),
    setValue,
    fieldNames: {
      postalCode: 'postal_code',
      address: 'address1',
    },
    addressSearchMutation,
  });

  const {
    isAddressModalOpen: isFounderAddressModalOpen,
    closeAddressModal: closeFounderAddressModal,
    handleAddressButtonClick: handleFounderAddressButtonClick,
    handleAddressSelect: handleFounderAddressSelect,
    modalInput: founderModalInput,
  } = useAddressSearchModalHelper<ClinicLocationFormData>({
    postalCodeValue: String(founderPostalCode ?? ''),
    addressValue: String(founderAddress1 ?? ''),
    setValue,
    fieldNames: {
      postalCode: 'founder_postal_code',
      address: 'founder_address1',
    },
    addressSearchMutation,
  });

  const onWarekiDateChange = (field: (v: string) => void) => (warekiStr: string) => {
    if (!warekiStr?.trim()) {
      field('');
      return;
    }
    const seireki = convertSeirekiWrap(warekiStr);
    const d = dayjs(seireki.replace(/\//g, '-'));
    field(d.isValid() ? d.format('YYYY-MM-DD') : '');
  };

  const onSubmitWithErrorLog = handleSubmit(
    data => {
      const clinicId = Number(getValues('clinic_id'));
      return onSubmit({ ...data, clinic_id: clinicId });
    }
  );

  return (
    <form onSubmit={onSubmitWithErrorLog} className="space-y-6">
      <div className="p-4 bg-white border border-gray-200 rounded shadow-sm">
        <h4 className="mb-4 text-lg font-medium">
          {isEditMode
            ? editDisplayId
              ? `拠点更新:${editDisplayId}`
              : editSerialNumber != null
                ? `拠点更新:${editSerialNumber}`
                : '拠点編集'
            : '拠点登録'}
        </h4>

        {/* 治療院ID */}
        <div className="mb-4">
          <AppFormLabel className="mb-2" label={<>治療院ID {!isEditMode && <RequiredTag />}</>} />
          <Controller
            name="clinic_id"
            control={control}
            render={({ field }) => {
              const isClinicFixed =
                showClinicIdDisabled &&
                clinicIdFromUrl != null &&
                clinicIdFromUrl > 0 &&
                clinics.some(c => sameClinicId(c.id, clinicIdFromUrl));
              return (
                <AppSelect
                  {...field}
                  id="clinic_id"
                  className="w-full"
                  options={clinics.map(c => ({ value: c.id, label: c.clinic_number }))}
                  value={
                    isClinicFixed
                      ? (clinicIdFromUrl ?? (field.value ? Number(field.value) : undefined))
                      : field.value
                        ? Number(field.value)
                        : undefined
                  }
                  onChange={v => {
                    const num = v === undefined || v === null ? 0 : Number(v);
                    if (num > 0) userHasSelectedClinicRef.current = true;
                    field.onChange(num);
                  }}
                  onClear={() => {
                    userClearedClinicIdRef.current = true;
                    field.onChange(0);
                  }}
                  disabled={isClinicFixed}
                  allowClear={!isClinicFixed}
                  status={errors.clinic_id ? 'error' : undefined}
                  placeholder=""
                />
              );
            }}
          />
          {errors.clinic_id && (
            <p className="mt-1 text-sm text-red-600">{errors.clinic_id.message}</p>
          )}
        </div>

        {/* サービス区分 - create: after 治療院ID; edit: use clinic from initialData */}
        {!isEditMode ? (
          <div className="mb-4">
            <AppFormLabel
              className="mb-2"
              label={
                <>
                  サービス区分 <RequiredTag />
                </>
              }
            />
            <div className="flex flex-wrap gap-6 pt-2">
              <Controller
                name="service_type"
                control={control}
                render={({ field }) => {
                  const a2Checked =
                    field.value === SERVICE_TYPE_A2 || field.value === SERVICE_TYPE_BOTH;
                  const r2Checked =
                    field.value === SERVICE_TYPE_R2 || field.value === SERVICE_TYPE_BOTH;
                  return (
                    <>
                      <AppCheckbox
                        checked={a2Checked}
                        disabled={isSingleServiceClinic}
                        onChange={e => {
                          const a2 = e.target.checked;
                          const r2 = isR2Only ? false : r2Checked;
                          field.onChange(getServiceTypeFromCheckboxes(a2, r2));
                        }}
                      >
                        A2（はり・きゅう）
                      </AppCheckbox>
                      <AppCheckbox
                        checked={r2Checked}
                        disabled={isSingleServiceClinic}
                        onChange={e => {
                          const r2 = e.target.checked;
                          const a2 = isA2Only ? true : a2Checked;
                          field.onChange(getServiceTypeFromCheckboxes(a2, r2));
                        }}
                      >
                        R2（マッサージ）
                      </AppCheckbox>
                    </>
                  );
                }}
              />
            </div>
            {errors.service_type && (
              <p className="mt-1 text-sm text-red-600">{errors.service_type.message}</p>
            )}
          </div>
        ) : (
          <div className="mb-4">
            <span className={`${LABEL_WIDTH} pt-2 text-sm text-gray-700`}>サービス区分</span>
            <div className="flex flex-wrap gap-6 pt-2">
              <Controller
                name="service_type"
                control={control}
                render={({ field }) => {
                  const a2Checked =
                    field.value === SERVICE_TYPE_A2 || field.value === SERVICE_TYPE_BOTH;
                  const r2Checked =
                    field.value === SERVICE_TYPE_R2 || field.value === SERVICE_TYPE_BOTH;
                  const init = initialData as ClinicLocationInitialData;
                  // Use clinic's service_type (what clinic supports) for enable/disable.
                  // selected value (a2Checked/r2Checked) comes from field.value (clinic location data).
                  const clinicFromInit = init?.clinic_id
                    ? findClinicById(clinics, init.clinic_id)
                    : null;
                  const clinicServiceType = (clinicFromInit?.service_type ?? '').toUpperCase();
                  const editA2Only = clinicServiceType === SERVICE_TYPE_A2;
                  const editR2Only = clinicServiceType === SERVICE_TYPE_R2;
                  const editSingleService = editA2Only || editR2Only;
                  const a2Disabled = editSingleService || (init?.has_visits_a2 ?? false) || (init?.has_massager_a2 ?? false);
                  const r2Disabled = editSingleService || (init?.has_visits_r2 ?? false) || (init?.has_massager_r2 ?? false);
                  return (
                    <>
                      <AppCheckbox
                        checked={a2Checked}
                        disabled={a2Disabled}
                        onChange={e => {
                          const a2 = e.target.checked;
                          const r2 = editR2Only ? false : r2Checked;
                          field.onChange(getServiceTypeFromCheckboxes(a2, r2));
                        }}
                      >
                        A2（はり・きゅう）
                      </AppCheckbox>
                      <AppCheckbox
                        checked={r2Checked}
                        disabled={r2Disabled}
                        onChange={e => {
                          const r2 = e.target.checked;
                          const a2 = editA2Only ? true : a2Checked;
                          field.onChange(getServiceTypeFromCheckboxes(a2, r2));
                        }}
                      >
                        R2（マッサージ）
                      </AppCheckbox>
                    </>
                  );
                }}
              />
            </div>
            {errors.service_type && (
              <p className="mt-1 text-sm text-red-600">{errors.service_type.message}</p>
            )}
          </div>
        )}

        {/* 拠点名 + 拠点名カナ — IME auto-kana (R2-style), same behavior as 医療機関 modal */}
        <div className="flex items-start gap-8 mb-4">
          <div className="flex items-start flex-1">
            <span className={`${LABEL_WIDTH} pt-2 text-sm text-gray-700`}>
              拠点名 <RequiredTag />
            </span>
            <div className="flex-1">
              <Controller
                name="location_name"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    maxLength={CLINIC_NAME_FIELD_MAX}
                    {...locationNameKanaFields.nameInputProps}
                    className="w-full"
                    status={errors.location_name ? 'error' : undefined}
                  />
                )}
              />
              {errors.location_name && (
                <p className="mt-1 text-sm text-red-600">{errors.location_name.message}</p>
              )}
            </div>
          </div>
          <div className="flex items-start flex-1">
            <span className="w-40 pt-2 pl-8 text-sm text-gray-700 shrink-0">拠点名カナ</span>
            <div className="flex-1">
              <Controller
                name="location_name_kana"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    maxLength={CLINIC_NAME_FIELD_MAX}
                    {...locationNameKanaFields.kanaInputProps}
                    className="w-full"
                    status={errors.location_name_kana ? 'error' : undefined}
                  />
                )}
              />
              {errors.location_name_kana && (
                <p className="mt-1 text-sm text-red-600">{errors.location_name_kana.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* 有効期限 */}
        <div className="flex items-start mb-4">
          <span className={`${LABEL_WIDTH} pt-2 text-sm text-gray-700`}>
            有効期限 <RequiredTag />
          </span>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Controller
                name="available_from"
                control={control}
                render={({ field }) => (
                  <RA2NDatePicker
                    className="w-44"
                    value={field.value ? convGtJDate(dayjs(field.value)) : ''}
                    onChange={onWarekiDateChange(field.onChange)}
                    error={!!errors.available_from}
                    placeholder=""
                  />
                )}
              />
              <span className="text-gray-500">から</span>
              <Controller
                name="available_to"
                control={control}
                render={({ field }) => (
                  <RA2NDatePicker
                    className="w-44"
                    value={field.value ? convGtJDate(dayjs(field.value)) : ''}
                    onChange={onWarekiDateChange(field.onChange)}
                    error={!!errors.available_to}
                    placeholder=""
                  />
                )}
              />
            </div>
            {(errors.available_from || errors.available_to) && (
              <p className="mt-1 text-sm text-red-600">
                {errors.available_from?.message ?? errors.available_to?.message}
              </p>
            )}
          </div>
        </div>

        {/* 保健所登録区分 */}
        <div className="flex items-start mb-4">
          <span className={`${LABEL_WIDTH} pt-2 text-sm text-gray-700`}>
            保健所登録区分 <RequiredTag />
          </span>
          <div className="flex-1 pt-2">
            <Controller
              name="health_center_regist_type_id"
              control={control}
              render={({ field }) => (
                <AppRadioGroup
                  value={field.value}
                  onChange={e => field.onChange(Number(e.target.value))}
                  className="flex flex-wrap gap-4"
                >
                  {healthCenterRegistTypes.map(t => (
                    <AppRadio key={t.id} value={t.id}>
                      {t.name}
                    </AppRadio>
                  ))}
                </AppRadioGroup>
              )}
            />
            {errors.health_center_regist_type_id && (
              <p className="mt-1 text-sm text-red-600">
                {errors.health_center_regist_type_id.message}
              </p>
            )}
          </div>
        </div>

        <hr className="my-4 border-gray-200" />

        {/* 郵便番号 */}
        <div className="flex items-start mb-4">
          <span className={`${LABEL_WIDTH} pt-2 text-sm text-gray-700`}>
            郵便番号 <RequiredTag />
          </span>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Controller
                name="postal_code"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    value={field.value ?? ''}
                    onChange={e => field.onChange(normalizePostalCodeInput(e.target.value))}
                    className="w-32"
                    status={errors.postal_code ? 'error' : undefined}
                  />
                )}
              />
              <AppButton type="primary" onClick={handleAddressButtonClick}>
                住所補助
              </AppButton>
            </div>
            {errors.postal_code && (
              <p className="mt-1 text-sm text-red-600">{errors.postal_code.message}</p>
            )}
          </div>
        </div>

        {/* 住所 (3 inputs) */}
        <div className="flex items-start mb-4">
          <span className={`${LABEL_WIDTH} pt-2 text-sm text-gray-700`}>
            住所 <RequiredTag />
          </span>
          <div className="flex flex-1 gap-2">
            <div className="flex-1">
              <Controller
                name="address1"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    className="w-full"
                    status={errors.address1 ? 'error' : undefined}
                  />
                )}
              />
              {errors.address1 && (
                <p className="mt-1 text-sm text-red-600">{errors.address1.message}</p>
              )}
            </div>
            <div className="flex-1">
              <Controller
                name="address2"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    className="w-full"
                    status={errors.address2 ? 'error' : undefined}
                  />
                )}
              />
              {errors.address2 && (
                <p className="mt-1 text-sm text-red-600">{errors.address2.message}</p>
              )}
            </div>
            <div className="flex-1">
              <Controller
                name="address3"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    disabled
                    className="w-full"
                    placeholder="建物名・部屋番号"
                  />
                )}
              />
            </div>
          </div>
        </div>

        {/* レセプト表示住所 + 住所拠点名 */}
        <div className="flex items-start gap-8 mb-4">
          <div className="flex items-start flex-1">
            <span className={`${LABEL_WIDTH} pt-2 text-sm text-gray-700`}>レセプト表示住所</span>
            <div className="flex-1">
              <Controller
                name="rezept_address"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    className="w-full"
                    status={errors.rezept_address ? 'error' : undefined}
                  />
                )}
              />
              {errors.rezept_address && (
                <p className="mt-1 text-sm text-red-600">{errors.rezept_address.message}</p>
              )}
            </div>
          </div>
          <div className="flex items-start flex-1">
            <span className="w-40 pt-2 pl-8 text-sm text-gray-700 shrink-0">住所拠点名</span>
            <div className="flex-1">
              <Controller
                name="base_name"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    className="w-full"
                    status={errors.base_name ? 'error' : undefined}
                  />
                )}
              />
              {errors.base_name && (
                <p className="mt-1 text-sm text-red-600">{errors.base_name.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* 電話番号 + FAX */}
        <div className="flex items-start gap-8 mb-4">
          <div className="flex items-start flex-1">
            <span className={`${LABEL_WIDTH} pt-2 text-sm text-gray-700`}>
              電話番号 <RequiredTag />
            </span>
            <div className="flex-1">
              <Controller
                name="telephone"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    onChange={e => field.onChange(e.target.value)}
                    className="w-full"
                    status={errors.telephone ? 'error' : undefined}
                  />
                )}
              />
              {errors.telephone && (
                <p className="mt-1 text-sm text-red-600">{errors.telephone.message}</p>
              )}
            </div>
          </div>
          <div className="flex items-start flex-1">
            <span className="w-40 pt-2 pl-8 text-sm text-gray-700 shrink-0">FAX</span>
            <div className="flex-1">
              <Controller
                name="fax"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    onChange={e => field.onChange(e.target.value)}
                    className="w-full"
                    status={errors.fax ? 'error' : undefined}
                  />
                )}
              />
              {errors.fax && <p className="mt-1 text-sm text-red-600">{errors.fax.message}</p>}
            </div>
          </div>
        </div>

        <hr className="my-4 border-gray-200" />

        {/* 法人個人 */}
        <div className="flex items-start mb-4">
          <span className={`${LABEL_WIDTH} pt-2 text-sm text-gray-700`}>法人個人</span>
          <div className="flex-1 pt-2">
            <Controller
              name="is_compnay"
              control={control}
              render={({ field }) => (
                <AppRadioGroup
                  value={field.value}
                  onChange={e => field.onChange(Number(e.target.value))}
                  className="flex gap-4"
                >
                  <AppRadio value={1}>法人</AppRadio>
                  <AppRadio value={0}>個人</AppRadio>
                </AppRadioGroup>
              )}
            />
            {errors.is_compnay && (
              <p className="mt-1 text-sm text-red-600">{errors.is_compnay.message}</p>
            )}
          </div>
        </div>

        {/* 開設者名 + 開設者名カナ — same IME + カナ normalization as 拠点名 */}
        <div className="flex items-start gap-8 mb-4">
          <div className="flex items-start flex-1">
            <span className={`${LABEL_WIDTH} pt-2 text-sm text-gray-700`}>開設者名</span>
            <div className="flex-1">
              <Controller
                name="founder_name"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    maxLength={CLINIC_NAME_FIELD_MAX}
                    {...founderNameKanaFields.nameInputProps}
                    className="w-full"
                    status={errors.founder_name ? 'error' : undefined}
                  />
                )}
              />
              {errors.founder_name && (
                <p className="mt-1 text-sm text-red-600">{errors.founder_name.message}</p>
              )}
            </div>
          </div>
          <div className="flex items-start flex-1">
            <span className="w-40 pt-2 pl-8 text-sm text-gray-700 shrink-0">開設者名カナ</span>
            <div className="flex-1">
              <Controller
                name="founder_name_kana"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    maxLength={CLINIC_NAME_FIELD_MAX}
                    {...founderNameKanaFields.kanaInputProps}
                    className="w-full"
                    status={errors.founder_name_kana ? 'error' : undefined}
                  />
                )}
              />
              {errors.founder_name_kana && (
                <p className="mt-1 text-sm text-red-600">{errors.founder_name_kana.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* 開設者郵便番号 */}
        <div className="flex items-start mb-4">
          <span className={`${LABEL_WIDTH} pt-2 text-sm text-gray-700`}>開設者郵便番号</span>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <Controller
                name="founder_postal_code"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    value={field.value ?? ''}
                    onChange={e => field.onChange(normalizePostalCodeInput(e.target.value))}
                    className="w-32"
                    status={errors.founder_postal_code ? 'error' : undefined}
                  />
                )}
              />
              <AppButton type="primary" onClick={handleFounderAddressButtonClick}>
                住所補助
              </AppButton>
            </div>
            {errors.founder_postal_code && (
              <p className="mt-1 text-sm text-red-600">{errors.founder_postal_code.message}</p>
            )}
          </div>
        </div>

        {/* 開設者住所 (3 inputs) */}
        <div className="flex items-start mb-4">
          <span className={`${LABEL_WIDTH} pt-2 text-sm text-gray-700`}>開設者住所</span>
          <div className="flex flex-1 gap-2">
            <div className="flex-1">
              <Controller
                name="founder_address1"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    className="w-full"
                    status={errors.founder_address1 ? 'error' : undefined}
                  />
                )}
              />
              {errors.founder_address1 && (
                <p className="mt-1 text-sm text-red-600">{errors.founder_address1.message}</p>
              )}
            </div>
            <div className="flex-1">
              <Controller
                name="founder_address2"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    className="w-full"
                    status={errors.founder_address2 ? 'error' : undefined}
                  />
                )}
              />
              {errors.founder_address2 && (
                <p className="mt-1 text-sm text-red-600">{errors.founder_address2.message}</p>
              )}
            </div>
            <div className="flex-1">
              <Controller
                name="founder_address3"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    className="w-full"
                    status={errors.founder_address3 ? 'error' : undefined}
                  />
                )}
              />
              {errors.founder_address3 && (
                <p className="mt-1 text-sm text-red-600">{errors.founder_address3.message}</p>
              )}
            </div>
          </div>
        </div>

        {/* 開設者電話番号 + 開設者生年月日 */}
        <div className="flex items-start gap-8 mb-4">
          <div className="flex items-start flex-1">
            <span className={`${LABEL_WIDTH} pt-2 text-sm text-gray-700`}>開設者電話番号</span>
            <div className="flex-1">
              <Controller
                name="founder_telephone"
                control={control}
                render={({ field }) => (
                  <AppTextField
                    {...field}
                    onChange={e => field.onChange(e.target.value)}
                    className="w-full"
                    status={errors.founder_telephone ? 'error' : undefined}
                  />
                )}
              />
              {errors.founder_telephone && (
                <p className="mt-1 text-sm text-red-600">{errors.founder_telephone.message}</p>
              )}
            </div>
          </div>
          <div className="flex items-start flex-1">
            <span className="w-40 pt-2 pl-8 text-sm text-gray-700 shrink-0">開設者生年月日</span>
            <div className="flex-1">
              <Controller
                name="founder_date_of_birth"
                control={control}
                render={({ field }) => (
                  <RA2NDatePicker
                    className="w-full"
                    value={field.value ? convGtJDate(dayjs(field.value)) : ''}
                    onChange={onWarekiDateChange(field.onChange)}
                    placeholder=""
                  />
                )}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 pt-2">
        <AppButton type="primary" htmlType="submit" className="min-w-[120px]" loading={isLoading}>
          更新
        </AppButton>
        {showBackToDetailButton && (
          <AppButton type="default" onClick={onCancel} className="min-w-[160px]">
            詳細画面へ戻る
          </AppButton>
        )}
      </div>

      <div onClick={e => e.stopPropagation()}>
        <AddressSearchModal
          isOpen={isAddressModalOpen}
          onClose={closeAddressModal}
          onSelect={handleAddressSelect}
          initialPostalCode1={modalInput.initialPostalCode1}
          initialPostalCode2={modalInput.initialPostalCode2}
          initialAddress={modalInput.initialAddress}
          title="住所補助"
          siteType={siteType}
          isAdmin={true}
        />
      </div>
      <div onClick={e => e.stopPropagation()}>
        <AddressSearchModal
          isOpen={isFounderAddressModalOpen}
          onClose={closeFounderAddressModal}
          onSelect={handleFounderAddressSelect}
          initialPostalCode1={founderModalInput.initialPostalCode1}
          initialPostalCode2={founderModalInput.initialPostalCode2}
          initialAddress={founderModalInput.initialAddress}
          title="開設者住所補助"
          siteType={siteType}
          isAdmin={true}
        />
      </div>
    </form>
  );
}
